import React, { Component } from 'react';
import { Grid, Form, Row, Col, Button } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { reduxForm, Field } from 'redux-form';
import MultiselectTwoSides from '@fortress-technology-solutions/react-multiselect-two-sides';
import ElementWithPermissions from '../../../components/ElementWithPermissions';
import SubmitButton from '../../../components/SubmitButton';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import type {
  User,
  UserStatus,
  UserTitle,
  UserRole,
  Property,
} from '../../App/types';
import {
  renderTextField,
  renderCheckboxField,
  renderSelectField,
} from '../../../utils/redux-form-helper';
import messages from '../messages';
import validate from './validate';
import AssignUserProperty from '../AssignUserProperty';
import { Typography } from '@fortress-technology-solutions/fortress-component-library/Atoms';

export const FirstName = ({ intl }: Object) => {
  return (
    <Col xs={12}>
      <Typography variant="titleLarge">
        <FormattedMessage {...messages.firstName} />
      </Typography>
      <section>
        <Field
          name="firstName"
          component={renderTextField}
          bsSize="lg"
          value="{firstName}"
          placeholder={intl.formatMessage(messages.firstName)}
        />
      </section>
    </Col>
  );
};

export const LastName = ({ intl }: Object) => {
  return (
    <Col xs={12}>
      <Typography variant="titleLarge">
        <FormattedMessage {...messages.lastName} />
      </Typography>
      <section>
        <Field
          name="lastName"
          component={renderTextField}
          bsSize="lg"
          placeholder={intl.formatMessage(messages.lastName)}
        />
      </section>
    </Col>
  );
};

export const EmailAddress = ({ intl }: Object) => {
  return (
    <Col xs={12}>
      <Typography variant="titleLarge">
        <FormattedMessage {...messages.emailAddress} />
      </Typography>
      <section>
        <Field
          name="emailAddress"
          component={renderTextField}
          bsSize="lg"
          placeholder={intl.formatMessage(messages.emailAddress)}
        />
      </section>
    </Col>
  );
};

export const Username = ({ intl }: Object) => {
  return (
    <Col xs={12}>
      <Typography variant="titleLarge">
        <FormattedMessage {...messages.username} />
      </Typography>

      <section>
        <Field
          name="username"
          component={renderTextField}
          bsSize="lg"
          className="input-lg"
          placeholder={intl.formatMessage(messages.username)}
        />
      </section>
    </Col>
  );
};

export const FieldUserStatus = ({ userStatuses }: Object) => {
  return (
    <Col xs={12}>
      <Typography variant="titleLarge">
        <FormattedMessage {...messages.userStatus} />
      </Typography>

      <section>
        <Field
          name="userStatusId"
          component={renderSelectField}
          options={userStatuses}
          bsSize="lg"
        />
      </section>
    </Col>
  );
};

export const FieldUserTitle = ({ userTitles }: Object) => {
  return (
    <Col xs={12}>
      <Typography variant="titleLarge">
        <FormattedMessage {...messages.userTitle} />
      </Typography>
      <section>
        <Field
          name="userTitleId"
          component={renderSelectField}
          options={userTitles}
          bsSize="lg"
        />
      </section>
    </Col>
  );
};

export const FieldUserRole = ({ userRoles }: Object) => {
  return (
    <Col xs={12}>
      <Typography variant="titleLarge">
        <FormattedMessage {...messages.userRole} />
      </Typography>
      <section>
        <Field
          name="userRoleId"
          component={renderSelectField}
          options={userRoles}
          bsSize="lg"
        />
      </section>
    </Col>
  );
};

type Props = {
  intl: any,
  user: User,
  userStatuses: Array<UserStatus>,
  userTitles: Array<UserTitle>,
  userRoles: Array<UserRole>,
  properties: Array<Property>,
  propertyList: Array<Property>,
  handleSubmit: Function,
  change: Function,
  valid: boolean,
  submitting: boolean,
  selectedProperties: Array<string>,
  handleMultiselectChange: Function,
  nonePropertiesSelected: boolean,
  hideMultiselect: boolean,
  toggleMultiselect: Function,
  handleCancel: Function,
  showErrorMessage: boolean,
  errorMessage: string,
  pristine: boolean,
  handlePropertiesSelectedError: Function,
  isSubmitting: boolean,
  allPropertiesSelected: boolean,
};

class EditUserForm extends Component<Props> {
  componentWillReceiveProps(nextProps) {
    if (nextProps !== this.props) {
      this.props.change('properties', nextProps.selectedProperties);
      if (nextProps.selectedProperties.length >= 1) {
        this.props.handlePropertiesSelectedError(false);
      }
    }
  }

  render() {
    const {
      intl,
      userStatuses,
      userTitles,
      userRoles,
      properties,
      handleSubmit,
      valid,
      selectedProperties,
      handleMultiselectChange,
      handleAllPropertiesSelection,
      handlePropertySelectionChange,
      nonePropertiesSelected,
      isSubmitting,
      propertyList,
      propertyClasses,
      organizationId,
      allPropertiesSelected,
      selectedPropertyGroups,
      handlePropertyGroupSelectionChange,
      flags,
    } = this.props;

    const selectedCount = selectedProperties.length;
    const availableCount = properties.length - selectedCount;
    const assignUserPropertyGroupFlag =
      flags?.adminEnhancementsUsersAssigntopropertytype ?? false;
    return (
      <Grid className="bodywrap col-centered" fluid={true}>
        <Form>
          <Row>
            <Col xs={12}>
              <h1
                className="title"
                style={{ display: 'inline-block', marginRight: '5px' }}
              >
                {intl.formatMessage(messages.header)}
              </h1>
              <h5 className="small" style={{ display: 'inline-block' }}>
                *<FormattedMessage {...messages.subheader} />
              </h5>
            </Col>
          </Row>
          <Row>
            <FirstName intl={intl} />
          </Row>
          <Row>
            <LastName intl={intl} />
          </Row>
          <Row>
            <EmailAddress intl={intl} />
          </Row>
          <Row>
            <Username intl={intl} />
          </Row>
          {assignUserPropertyGroupFlag ? (
            <AssignUserProperty
              properties={propertyList}
              propertyClasses={propertyClasses}
              intl={intl}
              organizationId={organizationId}
              selectedPropertyIds={selectedProperties}
              onAllPropertiesSelection={handleAllPropertiesSelection}
              allPropertiesSelected={allPropertiesSelected}
              handlePropertySelectionChange={handlePropertySelectionChange}
              handlePropertyGroupSelectionChange={
                handlePropertyGroupSelectionChange
              }
              selectedPropertyGroupIds={selectedPropertyGroups}
              noneSelected={nonePropertiesSelected}
              user={this.props.user}
              propertyGroupsDataFromProps={this.props.propertyGroupsData}
            />
          ) : (
            <Row>
              <Col xs={12}>
                <Typography variant="titleLarge">
                  <FormattedMessage {...messages.assignToProperty} />
                </Typography>
                <div>
                  <MultiselectTwoSides
                    className={[
                      'msts_theme_example',
                      this.props.hideMultiselect ? 'msts-disabled' : '',
                      nonePropertiesSelected ? 'msts-error' : '',
                    ].join(' ')}
                    onChange={handleMultiselectChange}
                    availableHeader="Available"
                    availableFooter={`Available: ${availableCount}`}
                    selectedHeader="Selected"
                    selectedFooter={`Selected: ${selectedCount}`}
                    placeholder="Filter…"
                    options={properties}
                    searchable={true}
                    showControls={true}
                    value={selectedProperties}
                  />
                </div>
                {nonePropertiesSelected && (
                  <span className="has-error">
                    Please select at least 1 property
                  </span>
                )}
                <Field
                  name="allProperties"
                  onChange={this.props.toggleMultiselect}
                  component={renderCheckboxField}
                  label={intl.formatMessage(messages.assignAllProperties)}
                  checked={this.props.hideMultiselect}
                  bsSize="lg"
                />
              </Col>
            </Row>
          )}
          <Row>
            <FieldUserStatus userStatuses={userStatuses} intl={intl} />
          </Row>
          <Row>
            <FieldUserTitle userTitles={userTitles} intl={intl} />
          </Row>
          <Row>
            <FieldUserRole userRoles={userRoles} intl={intl} />
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <Button
                onClick={this.props.handleCancel}
                className="pull-right btn-xs-block"
                type="button"
                bsStyle="default"
              >
                Cancel
              </Button>
            </Col>
            <Col xs={12} sm={6}>
              <ElementWithPermissions scope={['user-update']}>
                <SubmitButton
                  classes="btn btn-primary btn-xs-block"
                  disabled={isSubmitting || !valid}
                  clickHandler={handleSubmit}
                  isSubmitting={isSubmitting}
                >
                  Save Changes
                </SubmitButton>
              </ElementWithPermissions>
            </Col>
          </Row>
        </Form>
      </Grid>
    );
  }
}

EditUserForm = reduxForm({
  form: 'editUser',
  validate: validate,
  enableReinitialize: true,
})(EditUserForm);

export default withLDConsumer()(EditUserForm);
